<template>
    <Teleport to="body">
        <div v-if="verify.isOpen" class="fixed inset-0 z-50 bg-surface flex flex-col">
            <!-- Header -->
            <div class="flex items-center justify-between px-4 h-12 bg-surface-header text-white flex-shrink-0">
                <h2 class="font-semibold">{{ t('verify.title') }}</h2>
                <button class="p-2 -mr-2" :aria-label="t('verify.title')" @click="verify.close()">
                    <X class="w-6 h-6" />
                </button>
            </div>

            <div class="flex-1 overflow-y-auto p-4">
                <!-- Input phase: scanner + manual code -->
                <div v-if="!verify.result && !verify.loading" class="space-y-4">
                    <div class="bg-surface-card rounded overflow-hidden">
                        <BarcodeScanner v-if="scannerVisible" class="block w-full h-[40vh]" :on-detected="onDetected" />
                    </div>

                    <form class="space-y-3" @submit.prevent="submitCode">
                        <AppTextField
                            ref="inputRef"
                            v-model="code"
                            :label="t('verify.enterCode')"
                            autocorrect="off"
                            autocapitalize="none"
                        />
                        <AppButton type="submit" variant="success" large block :disabled="!code.trim().length">
                            {{ t('verify.verify') }}
                        </AppButton>
                    </form>

                    <p class="text-center text-sm opacity-60">{{ t('verify.scanHint') }}</p>
                </div>

                <!-- Loading -->
                <div v-else-if="verify.loading" class="py-10">
                    <AppProgressLinear />
                </div>

                <!-- Error / not found -->
                <div v-else-if="verify.error || !verify.result" class="space-y-4">
                    <div class="bg-danger text-white rounded px-4 py-3 text-center">
                        {{ t('verify.notFound') }}
                        <div v-if="verify.lastCode" class="text-sm opacity-90 mt-1 font-mono">
                            {{ verify.lastCode }}
                        </div>
                    </div>
                    <AppButton block large @click="verify.reset()">{{ t('verify.newSearch') }}</AppButton>
                </div>

                <!-- Result -->
                <div v-else class="space-y-4">
                    <div class="text-center font-mono text-sm opacity-70">{{ verify.lastCode }}</div>
                    <VerifyResult :data="verify.result" />
                    <AppButton block large @click="verify.reset()">{{ t('verify.newSearch') }}</AppButton>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
    import { computed, nextTick, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { X } from 'lucide-vue-next'
    import { useVerifyStore } from '@/stores/verify'
    import BarcodeScanner from '@/components/BarcodeScanner.vue'
    import VerifyResult from '@/components/VerifyResult.vue'
    import AppTextField from '@/components/ui/AppTextField.vue'
    import AppButton from '@/components/ui/AppButton.vue'
    import AppProgressLinear from '@/components/ui/AppProgressLinear.vue'

    const verify = useVerifyStore()
    const { t } = useI18n()

    const code = ref('')
    const inputRef = ref<InstanceType<typeof AppTextField> | null>(null)

    // Scanner runs only during the input phase while the dialog owns the camera.
    const scannerVisible = computed(() => verify.isOpen && verify.cameraBusy && !verify.result && !verify.loading)

    watch(
        () => verify.isOpen,
        open => {
            if (open) {
                code.value = ''
                // Intentionally do NOT focus the input — keep the on-screen keyboard hidden.
            }
        }
    )

    // When returning to input phase via "new search", clear the field.
    watch(
        () => verify.result,
        result => {
            if (!result && verify.isOpen) {
                code.value = ''
                void nextTick()
            }
        }
    )

    function onDetected(scanned: string) {
        if (verify.loading || verify.result) return
        void verify.loadInfo(scanned)
    }

    function submitCode() {
        const c = code.value.trim()
        if (!c) return
        void verify.loadInfo(c)
    }
</script>
